import { motion } from 'framer-motion';
import { useTeamStore } from '../store/useTeamStore';
import PropertyCard from '../components/PropertyCard';
import SearchBar from '../components/SearchBar';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import WhatsAppButton from '../components/WhatsAppButton';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Property } from '../types/property';
import { constructionPartners } from '../data/mockPartners';
import { Phone, Mail } from 'lucide-react';
import { fetchProperties, prefetchProperties } from '../services/api';

const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '/api' : 'http://localhost:3001/api');

export default function Home() {
  const teamMembers = useTeamStore(state => state.teamMembers);
  const [featuredProperties, setFeaturedProperties] = useState<Property[]>([]);
  const [saleProperties, setSaleProperties] = useState<Property[]>([]);
  const [rentProperties, setRentProperties] = useState<Property[]>([]);
  const [chacaraFloraProperties, setChacaraFloraProperties] = useState<Property[]>([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [showAllTeam, setShowAllTeam] = useState(false);
  const [heroImage, setHeroImage] = useState<string>('');

  const [activeTab, setActiveTab] = useState<'todos' | 'venda' | 'aluguel'>('todos');
  const brokerTeamMembers = (() => {
    const membersWithBrokerRole = teamMembers.filter((member) =>
      /corretor|corretora/i.test(member.role)
    );

    if (membersWithBrokerRole.length > 0) {
      return membersWithBrokerRole;
    }

    return teamMembers.filter((member) => member.creci?.trim());
  })();
  const allProperties = [...featuredProperties, ...saleProperties, ...rentProperties].filter(
    (property, index, items) => items.findIndex((item) => item.id === property.id) === index
  );
  const displayProperties =
    activeTab === 'todos'
      ? allProperties.slice(0, 6)
      : activeTab === 'venda'
        ? saleProperties
        : rentProperties;

  const prefetchDefaultPropertiesPage = () => {
    prefetchProperties({
      transaction: 'venda',
      page: 1,
      pageSize: 12,
    });
  };

  useEffect(() => {
    let cancelled = false;

    async function loadHomeProperties() {
      const [featured, sale, rent, chacara] = await Promise.all([
        fetchProperties({ featured: true, page: 1, pageSize: 6 }),
        fetchProperties({ transaction: 'venda', page: 1, pageSize: 6 }),
        fetchProperties({ transaction: 'aluguel', page: 1, pageSize: 6 }),
        fetchProperties({ neighborhood: 'Chácara Flora', page: 1, pageSize: 3 }),
      ]);

      if (cancelled) {
        return;
      }

      setFeaturedProperties(featured.items);
      setSaleProperties(sale.items);
      setRentProperties(rent.items);
      setChacaraFloraProperties(chacara.items);
    }

    loadHomeProperties();

    return () => {
      cancelled = true;
    };
  }, []);

  // Buscar imagem hero customizada ou usar imagem de imóvel aleatório
  useEffect(() => {
    const loadHeroImage = async () => {
      try {
        const response = await fetch(`${API_URL}/admin/hero-property`);
        if (response.ok) {
          const data = await response.json();
          if (data.imageUrl) {
            setHeroImage(data.imageUrl);
            return;
          }
        }
      } catch (error) {
        console.log('Sem imagem hero configurada, usando imagem de imóvel');
      }

      // Fallback: usar imagem de um imóvel aleatório
      const availableProperties = [...featuredProperties, ...saleProperties, ...rentProperties];
      if (availableProperties.length > 0) {
        const pool = featuredProperties.length > 0 ? featuredProperties : availableProperties;
        const randomIndex = Math.floor(Math.random() * pool.length);
        const randomProperty = pool[randomIndex];
        if (randomProperty && randomProperty.images.length > 0) {
          setHeroImage(randomProperty.images[0]);
        }
      }
    };

    loadHeroImage();
  }, [featuredProperties, saleProperties, rentProperties]);

  const quickFilters = [
    { image: 'https://public.youware.com/users-website-assets/prod/c66a8b9c-a024-493b-b5f3-204c25c8c941/dd61545104644be48941d20b2dcc2a43', title: 'Alto da Boa Vista', link: '/imoveis?neighborhood=alto-da-boa-vista' },
    { image: 'https://public.youware.com/users-website-assets/prod/c66a8b9c-a024-493b-b5f3-204c25c8c941/1e2f7e0602384244bb8f0981dd33cee0', title: 'Morumbi', link: '/imoveis?neighborhood=morumbi' },
    { image: 'https://public.youware.com/users-website-assets/prod/c66a8b9c-a024-493b-b5f3-204c25c8c941/51daedef17a749629eff1512270c5508', title: 'Panamby', link: '/imoveis?neighborhood=panamby' },
    { image: 'https://public.youware.com/users-website-assets/prod/c66a8b9c-a024-493b-b5f3-204c25c8c941/e170735053c84e96bfc2c517aef7acf8', title: 'Corporate', link: '/corporate' },
    { image: 'https://public.youware.com/users-website-assets/prod/c66a8b9c-a024-493b-b5f3-204c25c8c941/2143675f54594ac7933c54299640a592', title: 'Casa em Condomínio', link: '/imoveis?type=casa-condominio' },
    { image: 'https://public.youware.com/users-website-assets/prod/c66a8b9c-a024-493b-b5f3-204c25c8c941/1863ff5044014c74beeb25ffa15fc2d5', title: 'Cobertura', link: '/imoveis?type=cobertura' },
  ];

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Cadastro realizado com sucesso!');
    setEmail('');
    setName('');
    setPhone('');
  };

  return (
    <div>
      <WhatsAppButton />
      {/* Hero Section */}
      {heroImage && (
        <section className="relative h-screen overflow-hidden bg-black">
          <img
            src={heroImage}
            alt="Imagem principal"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div className="relative h-full flex flex-col items-center justify-center px-4 text-white">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <span
                className="text-5xl md:text-7xl lg:text-8xl text-white tracking-[0.08em]"
                style={{
                  fontFamily: '"Segoe UI Bold", "Segoe UI", sans-serif',
                  fontWeight: 700,
                  textShadow: '0 4px 24px rgba(0, 0, 0, 0.45)',
                }}
              >
                Siga Imóveis
              </span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Link to="/imoveis">
                <Button
                  variant="secondary"
                  size="lg"
                  onMouseEnter={prefetchDefaultPropertiesPage}
                  onFocus={prefetchDefaultPropertiesPage}
                  onTouchStart={prefetchDefaultPropertiesPage}
                >
                  VER IMÓVEIS
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Search Bar Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <SearchBar />
          </motion.div>
        </div>
      </section>

      {/* All Properties Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-serif text-forest-900 mb-8 text-center">
              Todos os Imóveis
            </h2>
            
            <div className="flex justify-center mb-8 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('todos')}
                className={`px-8 py-4 font-medium transition-colors ${
                  activeTab === 'todos'
                    ? 'text-forest-900 border-b-2 border-jungle-600'
                    : 'text-gray-500 hover:text-forest-900'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => setActiveTab('venda')}
                className={`px-8 py-4 font-medium transition-colors ${
                  activeTab === 'venda'
                    ? 'text-forest-900 border-b-2 border-jungle-600'
                    : 'text-gray-500 hover:text-forest-900'
                }`}
              >
                Para comprar
              </button>
              <button
                onClick={() => setActiveTab('aluguel')}
                className={`px-8 py-4 font-medium transition-colors ${
                  activeTab === 'aluguel'
                    ? 'text-forest-900 border-b-2 border-jungle-600'
                    : 'text-gray-500 hover:text-forest-900'
                }`}
              >
                Para alugar
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              {displayProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
            
            <div className="text-center">
              <Link to="/imoveis">
                <Button
                  variant="outline"
                  size="lg"
                  onMouseEnter={prefetchDefaultPropertiesPage}
                  onFocus={prefetchDefaultPropertiesPage}
                  onTouchStart={prefetchDefaultPropertiesPage}
                >
                  VER TODOS
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Chácara Flora Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-serif text-forest-900 mb-4 text-center">
              Destaque
            </h2>
            <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
              O mais exclusivo de São Paulo
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              {chacaraFloraProperties.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
            </div>
            
            <div className="text-center">
              <Link to="/chacara-flora">
                <Button variant="secondary" size="lg">
                  MAIS DESTAQUES
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Filters Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-serif text-forest-900 mb-4 text-center">
              Seleção Siga
            </h2>
            <p className="text-center text-gray-600 mb-8">
              Nossos imóveis mais procurados
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quickFilters.map((filter, index) => (
                <Link
                  key={index}
                  to={filter.link}
                  className="group relative h-64 overflow-hidden rounded-sm"
                >
                  <img
                    src={filter.image}
                    alt={filter.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                    <h3 className="text-white text-2xl font-semibold">
                      {filter.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                alt="Equipe Siga"
                className="w-full h-[400px] object-cover rounded-sm"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-serif text-forest-900 mb-6">
                SOBRE A SIGA
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                A nossa história tem muitos m² de sonhos realizados.
              </p>
              <p className="text-gray-700 leading-relaxed mb-8">
                A Siga Imóveis Sob Medida conta com profissionais que somam 20 anos de experiência 
                no mercado imobiliário. Oferecemos um serviço personalizado, focado em entender 
                suas necessidades e encontrar o imóvel ideal para você.
              </p>
              <Link to="/sobre">
                <Button variant="outline" size="lg">
                  SAIBA MAIS SOBRE NÓS
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-3xl font-serif text-forest-900 mb-4 text-center">
              Nossa Equipe
            </h2>
            <p className="text-center text-gray-600 mb-12">
              Conheça os profissionais que vão te ajudar a encontrar o imóvel ideal
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {(showAllTeam ? brokerTeamMembers : brokerTeamMembers.slice(0, 6)).map((member) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="group"
                >
                  <div className="relative overflow-hidden rounded-sm mb-2 aspect-square">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                      <div className="text-white space-y-0.5">
                        <div className="flex items-center gap-1">
                          <Phone size={12} />
                          <span className="text-[10px]">{member.phone}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Mail size={12} />
                          <span className="text-[10px] truncate">{member.email}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-forest-900 mb-0.5 truncate">
                    {member.name}
                  </h3>
                  <p className="text-xs text-jungle-600 mb-0.5 truncate">{member.role}</p>
                  <p className="text-[10px] text-gray-500">{member.creci}</p>
                </motion.div>
              ))}
            </div>

            {!showAllTeam && brokerTeamMembers.length > 6 && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setShowAllTeam(true)}
                  className="inline-flex items-center gap-2 text-jungle-600 hover:text-jungle-700 font-semibold transition-colors"
                >
                  Ver todos os corretores
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            )}

            {showAllTeam && brokerTeamMembers.length > 6 && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setShowAllTeam(false)}
                  className="inline-flex items-center gap-2 text-jungle-600 hover:text-jungle-700 font-semibold transition-colors"
                >
                  Ver menos
                  <svg className="w-5 h-5 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Neighborhoods Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-3xl font-serif text-forest-900 mb-4 text-center">
              Os Bairros mais buscados de São Paulo
            </h2>
            <p className="text-center text-gray-600 mb-12">
              Confira a lista dos endereços mais populares para quem deseja viver na capital.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4">
              <Link to="/imoveis?neighborhood=morumbi" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Morumbi
              </Link>
              <Link to="/imoveis?neighborhood=alto-da-boa-vista" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Alto da Boa Vista
              </Link>
              <Link to="/imoveis?neighborhood=panamby" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Panamby
              </Link>
              <Link to="/imoveis?neighborhood=brooklin" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Brooklin
              </Link>
              <Link to="/imoveis?neighborhood=vila-andrade" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Vila Andrade
              </Link>
              <Link to="/imoveis?neighborhood=vila-suzana" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Vila Suzana
              </Link>
              <Link to="/imoveis?neighborhood=campo-belo" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Campo Belo
              </Link>
              <Link to="/imoveis?neighborhood=jardim-guedala" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Jardim Guedala
              </Link>
              <Link to="/imoveis?neighborhood=granja-julieta" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Granja Julieta
              </Link>
              <Link to="/imoveis?neighborhood=chacara-santo-antonio" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Chácara Santo Antônio
              </Link>
              <Link to="/imoveis?neighborhood=real-parque" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Real Parque
              </Link>
              <Link to="/imoveis?neighborhood=jardim-prudencia" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Jardim Prudência
              </Link>
              <Link to="/imoveis?neighborhood=jardim-marajoara" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Jardim Marajoara
              </Link>
              <Link to="/imoveis?neighborhood=cidade-jardim" className="text-gray-700 hover:text-jungle-600 transition-colors underline">
                Cidade Jardim
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Construction Partners Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-3xl font-serif text-forest-900 mb-4 text-center">
              Construtoras Parceiras
            </h2>
            <p className="text-center text-gray-600 mb-12">
              Trabalhamos com as principais construtoras do mercado
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              {constructionPartners.map((partner) => (
                <motion.div
                  key={partner.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4 }}
                >
                  <a
                    href={partner.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-white p-8 rounded-sm shadow-sm hover:shadow-md transition-shadow group"
                  >
                    <div className="flex flex-col items-center justify-center h-full">
                      <img
                        src={partner.logo}
                        alt={partner.name}
                        className="w-full h-20 object-contain mb-4 transition-all duration-300"
                      />
                      <p className="text-center text-sm text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        {partner.description}
                      </p>
                    </div>
                  </a>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-white border-t border-gray-200">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-xl mx-auto"
          >
            <h2 className="text-3xl font-serif text-forest-900 mb-4 text-center">
              NEWSLETTER
            </h2>
            <p className="text-center text-gray-600 mb-8">
              Fique por dentro de todas as novidades
            </p>
            
            <form onSubmit={handleNewsletterSubmit} className="space-y-4">
              <Input
                placeholder="Nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="bg-white"
              />
              <Input
                type="tel"
                placeholder="Telefone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className="bg-white"
              />
              <Input
                type="email"
                placeholder="E-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white"
              />
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="privacy"
                  required
                  className="mt-1"
                />
                <label htmlFor="privacy" className="text-sm text-gray-600">
                  Li e concordo com a{' '}
                  <a href="/politica-privacidade" className="text-jungle-600 underline">
                    política de privacidade
                  </a>
                  .
                </label>
              </div>
              <Button type="submit" className="w-full bg-gray-300 text-gray-700 hover:bg-gray-400">
                ENVIAR
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
